import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Form as FinalForm, Field } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import classNames from 'classnames';
import { isEqual } from 'lodash';
import {
  Form,
  FieldSelect,
  FieldTextInput,
  FieldDateInput,
  SearchableMultiSelect,
  FieldFileUpload,
  SecondaryButton,
  IconTrashBin,
  IconArrowHead,
  ValidationError,
} from '../../components';
import { required, nonEmptyArray } from '../../util/validators';
import getCountryCodes from '../../translations/countryCodes';

import css from './AddActivityForm.module.css';

const requiredCheckbox = message => value => (value === true ? undefined : message);

const DocumentCard = ({ name, onRemove, intl, countryOptions, isCompany, form, isEditing }) => {
  const documentTypeValue = form.getFieldState(`${name}.documentType`)?.value;
  const requiredMsg = intl.formatMessage({ id: 'General.requiredField', defaultMessage: 'Required' });
  
  // Define document types based on provider type
  const individualDocumentTypes = [
    { key: 'professional_certificate', label: intl.formatMessage({ id: 'AddActivityForm.docTypeProfessionalCert', defaultMessage: 'Professional Certificate' }) },
    { key: 'association_registration', label: intl.formatMessage({ id: 'AddActivityForm.docTypeAssociationReg', defaultMessage: 'Association Registration' }) },
    { key: 'safety_certificate', label: intl.formatMessage({ id: 'AddActivityForm.docTypeSafetyCert', defaultMessage: 'Safety Certificate' }) },
    { key: 'insurance_coverage', label: intl.formatMessage({ id: 'AddActivityForm.docTypeInsuranceCoverage', defaultMessage: 'Proof of Insurance Coverage' }) },
  ];
  
  const companyDocumentTypes = [
    { key: 'chamber_of_commerce', label: intl.formatMessage({ id: 'AddActivityForm.docTypeChamberCommerce', defaultMessage: 'Chamber of Commerce' }) },
    { key: 'business_license', label: intl.formatMessage({ id: 'AddActivityForm.docTypeBusinessLicense', defaultMessage: 'Business License' }) },
    { key: 'insurance_coverage', label: intl.formatMessage({ id: 'AddActivityForm.docTypeInsuranceCoverage', defaultMessage: 'Proof of Insurance Coverage' }) },
  ];
  
  const documentTypes = isCompany ? companyDocumentTypes : individualDocumentTypes;
  
  // Field visibility configuration per document type
  const fieldConfig = React.useMemo(() => ({
    professional_certificate: {
      country: { show: true, required: true },
      issuer: { show: true, required: true },
      id: { show: false },
      issuerUrl: { show: false },
      expiry: { show: false }
    },
    association_registration: {
      country: { show: true, required: true },
      issuer: { show: true, required: true },
      id: { show: true, required: true },
      issuerUrl: { show: true, required: false },
      expiry: { show: true, required: false, prefill: true }
    },
    safety_certificate: {
      country: { show: true, required: true },
      issuer: { show: true, required: true },
      id: { show: false },
      issuerUrl: { show: false },
      expiry: { show: false }
    },
    insurance_coverage: {
      country: { show: false },
      issuer: { show: true, required: true },
      id: { show: true, required: true },
      issuerUrl: { show: false },
      expiry: { show: true, required: true, prefill: true }
    },
    chamber_of_commerce: {
      country: { show: true, required: true },
      issuer: { show: false },
      id: { show: true, required: true },
      issuerUrl: { show: true, required: false },
      expiry: { show: false }
    },
    business_license: {
      country: { show: true, required: true },
      issuer: { show: true, required: true },
      id: { show: true, required: true },
      issuerUrl: { show: true, required: false },
      expiry: { show: true, required: false }
    }
  }), []);
  
  const config = React.useMemo(() => fieldConfig[documentTypeValue] || {}, [fieldConfig, documentTypeValue]);
  const showAllFields = !!documentTypeValue;
  
  // Prefill expiry date to 1 year from now if needed
  React.useEffect(() => {
    if (documentTypeValue && config.expiry?.prefill) {
      const currentExpiry = form.getFieldState(`${name}.expiryDate`)?.value;
      if (!currentExpiry) {
        const oneYearFromNow = new Date();
        oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
        form.change(`${name}.expiryDate`, { date: oneYearFromNow });
      }
    }
  }, [documentTypeValue, name, form, config]);
  
  return (
    <div className={css.certificateCard}>
      <div className={css.cardHeader}>
        <Field name={`${name}.file`}>
          {({ input: { value }, meta }) => (
            <>
              <div className={css.fileName}>
                {value?.name || (value?.url ? 'Uploaded File' : 'No File')}
              </div>
              <div className={css.cardActions}>
                <FieldFileUpload
                  id={`${name}.file`}
                  name={`${name}.file`}
                  accept=".pdf,image/*"
                  label={intl.formatMessage({ id: 'AddActivityForm.modifyFile', defaultMessage: '✏️ Modify' })}
                  labelClassName={css.modifyButton}
                  className={css.modifyFileUpload}
                  multiple={false}
                  hidePlusIcon={true}
                />
                <button type="button" className={css.deleteButton} onClick={onRemove}>
                  <IconTrashBin className={css.deleteIcon} />
                  {intl.formatMessage({ id: 'AddActivityForm.deleteFile', defaultMessage: 'Delete' })}
                </button>
              </div>
            </>
          )}
        </Field>
      </div>
      
      <div className={css.cardFields}>
        <FieldSelect
          name={`${name}.documentType`}
          id={`${name}.documentType`}
          label={intl.formatMessage({ id: 'AddActivityForm.documentTypeLabel', defaultMessage: 'Document Type' })}
          className={css.field}
          validate={required(intl.formatMessage({ id: 'General.requiredField', defaultMessage: 'Required' }))}
        >
          <option value="">{intl.formatMessage({ id: 'AddActivityForm.selectDocumentType', defaultMessage: 'Select document type' })}</option>
          {documentTypes.map(dt => (
            <option key={dt.key} value={dt.key}>
              {dt.label}
            </option>
          ))}
        </FieldSelect>
        
        {showAllFields && (
          <>
             {config.country?.show && (
               <FieldSelect
                  name={`${name}.country`}
                  id={`${name}.country`}
                  label={intl.formatMessage({ id: 'AddActivityForm.countryLabel', defaultMessage: 'Country of Issue' })}
                  className={css.field}
                  validate={config.country.required ? required(requiredMsg) : null}
               >
                <option value="">{intl.formatMessage({ id: 'AddActivityForm.selectCountry', defaultMessage: 'Select country' })}</option>
                {countryOptions.map(c => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
               </FieldSelect>
             )}
             
             {config.issuer?.show && (
               <FieldTextInput
                  name={`${name}.issuer`}
                  id={`${name}.issuer`}
                  type="text"
                  label={intl.formatMessage({ 
                    id: 'AddActivityForm.issuingAuthorityLabel', 
                    defaultMessage: 'Issuing Authority / Organization' 
                  })}
                  placeholder={intl.formatMessage({ 
                    id: 'AddActivityForm.issuingAuthorityPlaceholder', 
                    defaultMessage: 'e.g. Italian Ski Federation' 
                  })}
                  className={css.field}
                  validate={config.issuer.required ? required(requiredMsg) : null}
               />
             )}

             {config.id?.show && (
               <FieldTextInput
                  name={`${name}.registerId`}
                  id={`${name}.registerId`}
                  type="text"
                  label={intl.formatMessage({ 
                    id: 'AddActivityForm.documentNumberLabel',
                    defaultMessage: 'Document/Certificate Number' 
                  })}
                  placeholder={intl.formatMessage({ 
                    id: 'AddActivityForm.numberPlaceholder', 
                    defaultMessage: 'e.g. 123456789' 
                  })}
                  className={css.field}
                  validate={config.id.required ? required(requiredMsg) : null}
               />
             )}

             {config.issuerUrl?.show && (
               <FieldTextInput
                  name={`${name}.issuerUrl`}
                  id={`${name}.issuerUrl`}
                  type="url"
                  label={intl.formatMessage({ id: 'AddActivityForm.issuerUrlLabel', defaultMessage: config.issuerUrl.required ? 'Issuer Registry URL' : 'Issuer Registry URL (optional)' })}
                  placeholder={intl.formatMessage({ id: 'AddActivityForm.issuerUrlPlaceholder', defaultMessage: 'e.g. https://example.com/registry/12345' })}
                  className={css.field}
                  validate={config.issuerUrl.required ? required(requiredMsg) : null}
               />
             )}

             {config.expiry?.show && (
               <FieldDateInput
                 name={`${name}.expiryDate`}
                 id={`${name}.expiryDate`}
                 label={intl.formatMessage({ id: 'AddActivityForm.expiryDateLabel', defaultMessage: config.expiry.required ? 'Valid Until' : 'Valid Until (optional)' })}
                 placeholderText={intl.formatMessage({ id: 'AddActivityForm.expiryDatePlaceholder', defaultMessage: 'Select date' })}
                 className={css.field}
                 useMobileMargins={false}
                 isOutsideRange={() => false}
                 validate={config.expiry.required ? required(requiredMsg) : null}
               />
             )}
          </>
        )}
      </div>
    </div>
  );
};

const AddActivityForm = props => {
  const {
    intl,
    activityOptions,
    getActivityDisciplines,
    rootClassName,
    className,
    updateError,
    onClearFormErrors,
    userIntent,
    isCompany,
    updateInProgress,
    hasCompletedLegalCompliance,
    ...restOfProps
  } = props;

  const fileInputRef = useRef();
  const [submittedValues, setSubmittedValues] = useState({});
  const [isAddDocumentOpen, setIsAddDocumentOpen] = useState(false);

  const classes = useMemo(() => classNames(rootClassName || css.root, className), [
    rootClassName,
    className,
  ]);

  const isProvider = userIntent === 'provider';

  const countryOptions = useMemo(() => {
    // Ensure we pass a valid locale string, defaulting to 'en' if undefined
    const locale = intl.locale || 'en';
    const codes = getCountryCodes(locale);
    return codes.sort((a, b) => a.name.localeCompare(b.name));
  }, [intl.locale]);

  const messages = useMemo(
    () => ({
      activityPlaceholder: intl.formatMessage({
        id: 'AddActivityForm.selectCategories',
      }),
      disciplinePlaceholder: intl.formatMessage({
        id: 'AddActivityForm.selectDisciplines',
      }),
      requiredField: intl.formatMessage({
        id: 'General.requiredField',
      }),
    }),
    [intl]
  );

  return (
    <FinalForm
      {...restOfProps}
      mutators={{ ...arrayMutators }}
      render={fieldRenderProps => {
        const { handleSubmit, form, invalid, values = {} } = fieldRenderProps;

        const submitInProgress = updateInProgress;
        const submittedOnce = Object.keys(submittedValues).length > 0;
        const pristineSinceLastSubmit = submittedOnce && isEqual(values, submittedValues);
        const submitDisabled = invalid || submitInProgress || pristineSinceLastSubmit;

        // Ensure selectedActivities is always an array
        const selectedActivities = Array.isArray(values.activity) ? values.activity : (values.activity ? [values.activity] : []);
        const allDisciplines = selectedActivities.reduce((acc, activityKey) => {
          const disciplines = getActivityDisciplines(activityKey) || [];
          return [...acc, ...disciplines];
        }, []);

        const uniqueDisciplines = allDisciplines.filter(
          (d, index, self) => index === self.findIndex(t => t.key === d.key)
        );

        const disciplineOptions = uniqueDisciplines.map(d => ({
          key: d.key,
          label: intl.formatMessage({ id: d.label }),
        }));

        const activityFieldState = form.getFieldState('activity');
        const disciplineFieldState = form.getFieldState('discipline');

        return (
          <Form
            className={classes}
            onSubmit={e => {
              setSubmittedValues(values);
              handleSubmit(e);
            }}
          >
            {/* FormEffectHandler removed in favor of direct onChange handling */}
            <p className={css.supportText}>
              <FormattedMessage
                id={isProvider ? 'AddActivityForm.providerSupportText' : 'AddActivityForm.clientSupportText'}
              />
            </p>

            <Field
              name="activity"
              validate={nonEmptyArray(messages.requiredField)}
              subscription={{}}
              render={() => null}
            />
            <div className={css.multiSelect}>
              <SearchableMultiSelect
                className={css.multiSelectField}
                inputClassName={css.multiSelectInput}
                id="activity"
                name="activity-search"
                label={messages.activityPlaceholder}
                placeholder={messages.activityPlaceholder}
                options={activityOptions}
                optionsId="activity-options"
                optionsName="activity"
                selectedOptions={selectedActivities}
                form={form}
              />
              <IconArrowHead direction="down" size="small" className={css.arrowIcon} />
            </div>
            {activityFieldState && <ValidationError fieldMeta={activityFieldState} />}

            {selectedActivities.length > 0 && (
              <>
                <Field
                  name="discipline"
                  validate={nonEmptyArray(messages.requiredField)}
                  subscription={{}}
                  render={() => null}
                />
                <div className={css.multiSelect}>
                  <SearchableMultiSelect
                    className={css.multiSelectField}
                    inputClassName={css.multiSelectInput}
                    id="discipline"
                    name="discipline-search"
                    label={messages.disciplinePlaceholder}
                    placeholder={messages.disciplinePlaceholder}
                    options={disciplineOptions}
                    optionsId="discipline-options"
                    optionsName="discipline"
                    selectedOptions={values.discipline || []}
                    form={form}
                  />
                  <IconArrowHead direction="down" size="small" className={css.arrowIcon} />
                </div>
                {disciplineFieldState && <ValidationError fieldMeta={disciplineFieldState} />}
              </>
            )}

            {isProvider && (
              <>
                <h3 className={css.sectionTitle}>
                  <FormattedMessage id="AddActivityForm.documentsTitle" defaultMessage="Documents" />
                </h3>
                <p className={css.sectionSubtitle}>
                  {isCompany ? (
                    <FormattedMessage 
                      id="AddActivityForm.companyDocsInfo" 
                      defaultMessage="Upload business documents for verification. Supported: PDF, Max 20MB."
                    />
                  ) : (
                    <FormattedMessage 
                      id="AddActivityForm.individualDocsInfo" 
                      defaultMessage="Upload professional documents for verification. Supported: PDF, Max 20MB."
                    />
                  )}
                </p>

                <FieldArray name="documents">
                  {({ fields }) => (
                    <div className={css.certificatesContainer}>
                      {fields.map((name, index) => (
                        <DocumentCard
                          key={name}
                          name={name}
                          onRemove={() => fields.remove(index)}
                          intl={intl}
                          countryOptions={countryOptions}
                          isCompany={isCompany}
                          form={form}
                          isEditing={!!(restOfProps.initialValues?.documents && restOfProps.initialValues.documents.length > 0)}
                        />
                      ))}
                    </div>
                  )}
                </FieldArray>

                {isAddDocumentOpen ? (
                  <div style={{ marginBottom: '32px' }}>
                    <div className={css.documentTypeSelector}>
                      <FieldSelect
                        name="pendingDocumentType"
                        id="pendingDocumentType"
                        label={intl.formatMessage({ id: 'AddActivityForm.selectDocumentType', defaultMessage: 'Select Document Type' })}
                        className={css.field}
                      >
                        <option value="">{intl.formatMessage({ id: 'AddActivityForm.chooseDocumentType', defaultMessage: 'Choose document type to upload' })}</option>
                        {(isCompany ? [
                          { key: 'chamber_of_commerce', label: intl.formatMessage({ id: 'AddActivityForm.docTypeChamberCommerce', defaultMessage: 'Chamber of Commerce' }) },
                          { key: 'business_license', label: intl.formatMessage({ id: 'AddActivityForm.docTypeBusinessLicense', defaultMessage: 'Business License' }) },
                          { key: 'insurance_coverage', label: intl.formatMessage({ id: 'AddActivityForm.docTypeInsuranceCoverage', defaultMessage: 'Proof of Insurance Coverage' }) },
                        ] : [
                          { key: 'professional_certificate', label: intl.formatMessage({ id: 'AddActivityForm.docTypeProfessionalCert', defaultMessage: 'Professional Certificate' }) },
                          { key: 'association_registration', label: intl.formatMessage({ id: 'AddActivityForm.docTypeAssociationReg', defaultMessage: 'Association Registration' }) },
                          { key: 'safety_certificate', label: intl.formatMessage({ id: 'AddActivityForm.docTypeSafetyCert', defaultMessage: 'Safety Certificate' }) },
                          { key: 'insurance_coverage', label: intl.formatMessage({ id: 'AddActivityForm.docTypeInsuranceCoverage', defaultMessage: 'Proof of Insurance Coverage' }) },
                        ]).map(dt => (
                          <option key={dt.key} value={dt.key}>
                            {dt.label}
                          </option>
                        ))}
                      </FieldSelect>
                    </div>

                    <Field name="pendingDocumentType" subscription={{ value: true }}>
                      {({ input: { value: selectedDocType } }) => (
                        selectedDocType ? (
                          <FieldFileUpload
                            id="newDocuments"
                            name="newDocuments"
                            accept=".pdf,image/*"
                            label={intl.formatMessage({ id: 'AddActivityForm.uploadDocument', defaultMessage: 'Upload Document' })}
                            labelClassName={classNames(css.whiteBg, css.addDocumentButton)}
                            className={css.formMargins}
                            fileInputRef={fileInputRef}
                            multiple={false}
                            onChange={files => {
                                if (files) {
                                    const fileArray = Array.isArray(files) ? files : [files];
                                    if (fileArray.length > 0) {
                                        const file = fileArray[0];
                                        form.mutators.push('documents', { 
                                          file, 
                                          documentType: selectedDocType,
                                          country: '', 
                                          issuer: '', 
                                          registerId: '', 
                                          issuerUrl: '', 
                                          expiryDate: '' 
                                        });
                                        setTimeout(() => {
                                            form.change('newDocuments', null);
                                            form.change('pendingDocumentType', '');
                                            if (fileInputRef.current) fileInputRef.current.value = '';
                                            setIsAddDocumentOpen(false);
                                        }, 100);
                                    }
                                }
                                onClearFormErrors();
                            }}
                          />
                        ) : null
                      )}
                    </Field>
                  </div>
                ) : (
                  // Hide "add another document" button only when editing an existing document (has initialValues with documents)
                  !(restOfProps.initialValues?.documents && restOfProps.initialValues.documents.length > 0) && (
                    <SecondaryButton type="button" onClick={() => setIsAddDocumentOpen(true)} className={css.formMargins}>
                      <FormattedMessage 
                        id={values.documents && values.documents.length > 0 ? "AddActivityForm.addAnotherDocument" : "AddActivityForm.addDocument"} 
                        defaultMessage={values.documents && values.documents.length > 0 ? "+ add another document" : "+ add document"}
                      />
                    </SecondaryButton>
                  )
                )}

                {/* Only show legal compliance section if user has documents AND hasn't confirmed all legal requirements yet */}
                {values.documents && values.documents.length > 0 && !hasCompletedLegalCompliance && (
                  <div className={css.selfCertificationSection}>
                    <h4 className={css.selfCertificationTitle}>
                      <FormattedMessage id="AddActivityForm.selfCertificationTitle" defaultMessage="Legal Declarations" />
                    </h4>
                    <p className={css.selfCertificationIntro}>
                      <FormattedMessage 
                        id="AddActivityForm.selfCertificationIntro" 
                        defaultMessage="Confirm the following declarations. These have legal value and are recorded with timestamp."
                      />
                    </p>
                    
                    <div className={css.checkboxGroup}>
                      {/* Data Accuracy - Both */}
                      <Field name="dataAccuracy" type="checkbox" validate={requiredCheckbox(messages.requiredField)}>
                        {({ input }) => (
                          <label className={css.checkboxLabel}>
                            <input {...input} type="checkbox" className={css.checkbox} />
                            <span className={css.checkboxText}>
                              <FormattedMessage 
                                id="AddActivityForm.dataAccuracy" 
                                defaultMessage="I certify that all information and documents provided are accurate and complete. I commit to promptly updating any outdated information."
                              />
                            </span>
                          </label>
                        )}
                      </Field>

                      {/* Liability + Intermediary Merged */}
                      <Field name="liabilityAcknowledgment" type="checkbox" validate={requiredCheckbox(messages.requiredField)}>
                        {({ input }) => (
                          <label className={css.checkboxLabel}>
                            <input {...input} type="checkbox" className={css.checkbox} />
                            <span className={css.checkboxText}>
                              <FormattedMessage 
                                id="AddActivityForm.liabilityAcknowledgment" 
                                defaultMessage="I acknowledge sole responsibility for all activity-related liability and maintain adequate insurance. I understand Crion acts only as a booking intermediary and assumes no liability."
                              />
                            </span>
                          </label>
                        )}
                      </Field>

                      {/* Criminal Record - Individual Only */}
                      {!isCompany && (
                        <Field name="criminalRecordsClearance" type="checkbox" validate={requiredCheckbox(messages.requiredField)}>
                          {({ input }) => (
                            <label className={css.checkboxLabel}>
                              <input {...input} type="checkbox" className={css.checkbox} />
                              <span className={css.checkboxText}>
                                <FormattedMessage 
                                  id="AddActivityForm.criminalRecordsClearance" 
                                  defaultMessage="I declare that I have no criminal record that would prevent me from providing these activities."
                                />
                              </span>
                            </label>
                          )}
                        </Field>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            {updateError}

            <SecondaryButton
              type="submit"
              className={css.submitButton}
              inProgress={submitInProgress}
              disabled={submitDisabled}
              ready={pristineSinceLastSubmit}
            >
              <FormattedMessage id="General.save" />
            </SecondaryButton>
          </Form>
        );
      }}
    />
  );
};

AddActivityForm.defaultProps = {
  rootClassName: null,
  className: null,
  updateInProgress: false,
  userIntent: 'customer',
  isCompany: false,
  hasCompletedLegalCompliance: false,
};

const { string, bool, func } = PropTypes;

AddActivityForm.propTypes = {
  rootClassName: string,
  className: string,
  updateInProgress: bool,
  intl: intlShape.isRequired,
  userIntent: string,
  isCompany: bool,
  hasCompletedLegalCompliance: bool,
  onClearFormErrors: func.isRequired,
  getActivityDisciplines: func.isRequired,
  onSubmit: func.isRequired,
};

export default injectIntl(AddActivityForm);
